import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { canViewPage, getFirstAllowedPath, type PermissionPage } from '../auth/permissions';
import { authApi } from '@services/api';
import { RouteLoadingFallback } from '@components/ui/Skeleton';

// Layouts
import { AuthLayout } from '@layouts/AuthLayout';
import { DashboardLayout } from '@layouts/DashboardLayout';

// Auth pages (eager for instant initial paint)
import { LoginPage } from '@pages/auth/LoginPage';
import { ForgotPasswordPage } from '@pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@pages/auth/ResetPasswordPage';
import { ReportConfirmationPage } from '@pages/public/ReportConfirmationPage';

// Lazy-loaded application pages for fast initial bundle & weak network resilience
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const XAIDashboardPage = lazy(() => import('@pages/xai/XAIDashboardPage').then((m) => ({ default: m.XAIDashboardPage })));

// Campaigns
const CampaignsPage = lazy(() => import('@pages/campaigns/CampaignsPage').then((m) => ({ default: m.CampaignsPage })));
const CreateCampaignPage = lazy(() => import('@pages/campaigns/CreateCampaignPage').then((m) => ({ default: m.CreateCampaignPage })));
const CampaignDetailPage = lazy(() => import('@pages/campaigns/CampaignDetailPage').then((m) => ({ default: m.CampaignDetailPage })));
const PhishingTemplateLibraryPage = lazy(() => import('@pages/campaigns/PhishingTemplateLibraryPage').then((m) => ({ default: m.PhishingTemplateLibraryPage })));
const TemplateDetailPage = lazy(() => import('@pages/campaigns/TemplateDetailPage').then((m) => ({ default: m.TemplateDetailPage })));
const TeachableMomentsPage = lazy(() => import('@pages/campaigns/TeachableMomentsPage').then((m) => ({ default: m.TeachableMomentsPage })));

// Training
const TrainingPage = lazy(() => import('@pages/training/TrainingPage').then((m) => ({ default: m.TrainingPage })));
const AssignmentDetailPage = lazy(() => import('@pages/training/AssignmentDetailPage').then((m) => ({ default: m.AssignmentDetailPage })));
const UpdateAssignmentPage = lazy(() => import('@pages/training/UpdateAssignmentPage').then((m) => ({ default: m.UpdateAssignmentPage })));
const AssessmentIntroPage = lazy(() => import('@pages/training/AssessmentIntroPage').then((m) => ({ default: m.AssessmentIntroPage })));
const AssessmentQuizPage = lazy(() => import('@pages/training/AssessmentQuizPage').then((m) => ({ default: m.AssessmentQuizPage })));
const AssessmentReviewPage = lazy(() => import('@pages/training/AssessmentReviewPage').then((m) => ({ default: m.AssessmentReviewPage })));

// Assessments / Questions
const QuestionBankPage = lazy(() => import('@pages/questions/QuestionBankPage').then((m) => ({ default: m.QuestionBankPage })));
const AssessmentFormPage = lazy(() => import('@pages/assessments/AssessmentFormPage').then((m) => ({ default: m.AssessmentFormPage })));

// Content
const ContentLibraryPage = lazy(() => import('@pages/content/ContentLibraryPage').then((m) => ({ default: m.ContentLibraryPage })));
const ContentDetailPage = lazy(() => import('@pages/content/ContentDetailPage').then((m) => ({ default: m.ContentDetailPage })));

// Users
const UsersPage = lazy(() => import('@pages/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => import('@pages/users/UserDetailPage').then((m) => ({ default: m.UserDetailPage })));
const CreateUserPage = lazy(() => import('@pages/users/CreateUserPage').then((m) => ({ default: m.CreateUserPage })));
const UpdateUserPage = lazy(() => import('@pages/users/UpdateUserPage').then((m) => ({ default: m.UpdateUserPage })));

// Groups
const GroupsPage = lazy(() => import('@pages/groups/GroupsPage').then((m) => ({ default: m.GroupsPage })));
const GroupDetailPage = lazy(() => import('@pages/groups/GroupDetailPage').then((m) => ({ default: m.GroupDetailPage })));
const CreateDynamicGroupPage = lazy(() => import('@pages/groups/CreateDynamicGroupPage').then((m) => ({ default: m.CreateDynamicGroupPage })));

// Reports
const ReportsPage = lazy(() => import('@pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const ReportViewerPage = lazy(() => import('@pages/reports/ReportViewerPage').then((m) => ({ default: m.ReportViewerPage })));

// Roles
const RolesPage = lazy(() => import('@pages/roles/RolesPage').then((m) => ({ default: m.RolesPage })));
const RoleFormPage = lazy(() => import('@pages/roles/RoleFormPage').then((m) => ({ default: m.RoleFormPage })));

// Settings
const SettingsPage = lazy(() => import('@pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));

// Profile
const ProfilePage = lazy(() => import('@pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));

// Guard
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user?.mustChangePassword && location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password?forced=true" replace />;
  }

  return <>{children}</>;
}

function FallbackRedirect() {
  const { user } = useAuthStore();
  const target = getFirstAllowedPath(user) || '/training';
  return <Navigate to={target} replace />;
}

function RolePrivateRoute({
  children,
  page,
  action = 'view',
}: {
  children: React.ReactNode;
  page: PermissionPage;
  action?: string;
}) {
  const { user, isAuthenticated } = useAuthStore();

  // Persisted auth state can hydrate in a separate render. Do not redirect
  // during that gap or a valid deep link can incorrectly fall back to dashboard.
  if (isAuthenticated && !user) return null;

  const isSuperAdmin = user?.roles?.includes('super_admin') === true;
  const hasPermission = action === 'view'
    ? canViewPage(user, page)
    : isSuperAdmin || user?.permissions?.[page]?.includes(action) === true;

  if (!hasPermission) {
    const fallback = getFirstAllowedPath(user) || '/training';
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}

function CampaignEditorRoute() {
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const isEditingDraft = Boolean(searchParams.get('id'));
  const isSuperAdmin = user?.roles?.includes('super_admin') === true;
  const action = isEditingDraft ? 'edit' : 'create';
  const hasPermission = isSuperAdmin || user?.permissions?.phishing_sim?.includes(action) === true;

  return hasPermission ? <CreateCampaignPage /> : <FallbackRedirect />;
}

export function AppRoutes() {
  const { isAuthenticated, user, setUser, logout } = useAuthStore();
  const [loadingUser, setLoadingUser] = useState(false);
  const requestStarted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || requestStarted.current) return;

    requestStarted.current = true;
    if (!user) {
      setLoadingUser(true);
    }
    authApi.me()
      .then((response) => {
        const serverUser = response.data?.data?.user ?? response.data?.data ?? response.data?.user ?? response.data;
        if (serverUser) setUser(serverUser);
        else logout();
      })
      .catch(() => logout())
      .finally(() => setLoadingUser(false));
  }, [isAuthenticated, logout, setUser]);

  if (isAuthenticated && !user && loadingUser) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading account…</div>;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/report-confirmation" element={<ReportConfirmationPage />} />
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <PrivateRoute>
            <DashboardLayout />
          </PrivateRoute>
        }
      >
        <Route path="/" element={<FallbackRedirect />} />
        <Route path="/dashboard" element={<RolePrivateRoute page="dashboard"><DashboardPage /></RolePrivateRoute>} />
        <Route path="/xai" element={<Navigate to="/dashboard?view=user" replace />} />

        {/* Campaigns */}
        <Route path="/campaigns" element={<RolePrivateRoute page="phishing_sim"><CampaignsPage /></RolePrivateRoute>} />
        <Route path="/campaigns/templates" element={<RolePrivateRoute page="templates"><PhishingTemplateLibraryPage /></RolePrivateRoute>} />
        <Route path="/campaigns/templates/:id" element={<RolePrivateRoute page="templates"><TemplateDetailPage /></RolePrivateRoute>} />
        <Route path="/campaigns/teachable-moments" element={<RolePrivateRoute page="phishing_sim"><TeachableMomentsPage /></RolePrivateRoute>} />
        <Route path="/campaigns/create" element={<CampaignEditorRoute />} />
        <Route path="/campaigns/:id" element={<RolePrivateRoute page="phishing_sim"><CampaignDetailPage /></RolePrivateRoute>} />

        {/* Training */}
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/training/assignments/:id" element={<AssignmentDetailPage />} />
        <Route path="/training/assignments/:id/edit" element={<UpdateAssignmentPage />} />
        <Route path="/training/modules/:id" element={<ContentDetailPage />} />
        <Route path="/training/assessments/:id" element={<AssessmentIntroPage />} />
        <Route path="/training/assessments/take/:attemptId" element={<AssessmentQuizPage />} />
        <Route
          path="/training/assessments/attempts/:attemptId/review"
          element={<AssessmentReviewPage />}
        />

        {/* Admin / Assessments */}
        <Route path="/admin/questions" element={<QuestionBankPage />} />
        <Route path="/admin/assessments/new" element={<AssessmentFormPage />} />
        <Route path="/admin/assessments/edit/:id" element={<AssessmentFormPage />} />

        {/* Content */}
        <Route path="/content" element={<RolePrivateRoute page="content_library"><ContentLibraryPage /></RolePrivateRoute>} />
        <Route path="/content/:id" element={<RolePrivateRoute page="content_library"><ContentDetailPage /></RolePrivateRoute>} />

        {/* Users */}
        <Route path="/users" element={<RolePrivateRoute page="users"><UsersPage /></RolePrivateRoute>} />
        <Route path="/users/create" element={<RolePrivateRoute page="users" action="create"><CreateUserPage /></RolePrivateRoute>} />
        <Route path="/users/:id/edit" element={<RolePrivateRoute page="users" action="edit"><UpdateUserPage /></RolePrivateRoute>} />
        <Route path="/users/:id" element={<RolePrivateRoute page="users"><UserDetailPage /></RolePrivateRoute>} />

        {/* Groups */}
        <Route path="/groups" element={<RolePrivateRoute page="groups"><GroupsPage /></RolePrivateRoute>} />
        <Route path="/groups/create" element={<RolePrivateRoute page="groups" action="create"><CreateDynamicGroupPage /></RolePrivateRoute>} />
        <Route path="/groups/:id" element={<RolePrivateRoute page="groups"><GroupDetailPage /></RolePrivateRoute>} />

        {/* Reports */}
        <Route path="/reports" element={<RolePrivateRoute page="reports"><ReportsPage /></RolePrivateRoute>} />
        <Route path="/reports/:id" element={<RolePrivateRoute page="reports"><ReportViewerPage /></RolePrivateRoute>} />

        {/* Roles */}
        <Route path="/roles" element={<RolePrivateRoute page="roles"><RolesPage /></RolePrivateRoute>} />
        <Route path="/roles/create" element={<RolePrivateRoute page="roles" action="create"><RoleFormPage /></RolePrivateRoute>} />
        <Route path="/roles/:id/edit" element={<RolePrivateRoute page="roles" action="edit"><RoleFormPage /></RolePrivateRoute>} />

        {/* Settings */}
        <Route path="/settings" element={<RolePrivateRoute page="settings"><SettingsPage /></RolePrivateRoute>} />

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<FallbackRedirect />} />
    </Routes>
  );
}
